import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Chart,
  LinearScale,
  LineElement,
  LineController,
  TimeSeriesScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ScatterController,
} from 'chart.js';
import { MatProgressBarModule } from '@angular/material/progress-bar';

Chart.register(
  LinearScale,
  LineElement,
  LineController,
  TimeSeriesScale,
  PointElement,
  ScatterController,
  Title,
  Tooltip,
  Legend,
);

type InputData = {
  yAxisLabel: string;
  dataLabel: string;
  tooltipAppend: string;
  data: Array<{ time: number; value: number }>;
};

export type ScatterAndLineChartDataInput = {
  scatterData: InputData;
  lineData: InputData;
  targetLineData: InputData;
  limitLineData: InputData;
};

type RawDataType = { x: number; y: number; tooltip: string };

type ChartDataType = RawDataType[];

interface TooltipType extends TooltipItem<'scatter' | 'line'> {
  raw: RawDataType;
}

@Component({
  selector: 'thred-scatterplot-and-line-chart',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule],
  templateUrl: './scatterplot-and-line-chart.component.html',
  styleUrl: './scatterplot-and-line-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScatterplotAndLineChartComponent implements OnChanges {
  @ViewChild('scatterplotAndLineChart') set canvas(canvas: ElementRef) {
    if (canvas) {
      this.myCanvas.set(canvas);
      this.createChart(canvas);
    }
  }

  myCanvas = signal<ElementRef | null>(null);
  private chart: Chart | undefined;

  @Input() chartTitle = '';
  @Input() xLabel = '';
  @Input() loading = signal(false);
  @Input({ required: true }) data: ScatterAndLineChartDataInput | null = null;

  ngOnChanges(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    const canvas = this.myCanvas();
    if (canvas) {
      this.createChart(canvas);
    }
  }

  createChart(canvas: ElementRef<HTMLCanvasElement>) {
    if (this.data) {
      const lineData: ChartDataType = [];
      const scatterData: ChartDataType = [];
      const targetLineData: ChartDataType = [];
      const limitLineData: ChartDataType = [];
      for (const { time, value } of this.data.targetLineData.data) {
        const t = new Date(time);
        const tooltipHourStart = t.getHours();
        const tooltipMinutes = t.getMinutes().toString().padStart(2, '0');
        const tooltip = `${value}${this.data.targetLineData.tooltipAppend} at ${tooltipHourStart}:${tooltipMinutes}`;

        targetLineData.push({
          x: t.getTime(),
          y: value,
          tooltip,
        });
      }

      for (const { time, value } of this.data.limitLineData.data) {
        const t = new Date(time);
        const tooltipHourStart = t.getHours();
        const tooltipMinutes = t.getMinutes().toString().padStart(2, '0');
        const tooltip = `${value}${this.data.limitLineData.tooltipAppend} at ${tooltipHourStart}:${tooltipMinutes}`;

        limitLineData.push({
          x: t.getTime(),
          y: value,
          tooltip,
        });
      }
      for (const { time, value } of this.data.lineData.data) {
        const t = new Date(time);
        const tooltipHourStart = t.getHours();
        const tooltipMinutes = t.getMinutes().toString().padStart(2, '0');
        const tooltip = `${value}${this.data.lineData.tooltipAppend} at ${tooltipHourStart}:${tooltipMinutes}`;

        lineData.push({
          x: t.getTime(),
          y: value,
          tooltip,
        });
      }

      for (const { time, value } of this.data.scatterData.data) {
        const x = new Date(time).getTime();
        const tooltip = `${value} ${this.data.scatterData.tooltipAppend}`;
        scatterData.push({ x, y: value, tooltip });
      }

      this.chart = new Chart(canvas.nativeElement, {
        data: {
          datasets: [
            {
              type: 'line',
              label: 'Target',
              data: targetLineData,
              borderColor: '#10645c',
              borderWidth: 1.5,
              pointRadius: 0,
            },
            {
              type: 'line',
              label: 'Limit',
              data: limitLineData,
              borderColor: '#ad1b1b',
              borderWidth: 1.5,
              pointRadius: 0,
            },
            {
              type: 'scatter',
              data: scatterData,
              label: this.data.scatterData.dataLabel,
              pointRadius: 2,
              backgroundColor: 'black',
            },
            {
              type: 'line',
              data: lineData,
              label: this.data.lineData.dataLabel,
              pointRadius: 0,
              pointHitRadius: 5,
              borderColor: 'grey',
              borderWidth: 2,
              yAxisID: 'y2',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                title: function ([context]: TooltipType[]) {
                  return context.raw.tooltip;
                },
              },
              displayColors: false,
            },
            title: {
              display: true,
              text: this.chartTitle,
              color: '#333333',
              font: {
                size: 23,
                weight: 'bold',
              },
            },
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                boxWidth: 20,
              },
            },
          },
          animation: {
            duration: 0,
          },
          scales: {
            x: {
              title: {
                display: true,
                text: this.xLabel,
              },
              type: 'time',
              time: {
                unit: 'minute',
              },
              grid: {
                drawOnChartArea: false,
              },
              offset: false,
            },
            y: {
              title: {
                display: true,
                text: this.data.scatterData.yAxisLabel,
              },
              type: 'linear',
              grid: {
                drawOnChartArea: true,
              },
              beginAtZero: true,
              min: 0,
            },
            y2: {
              title: {
                display: true,
                text: this.data.lineData.yAxisLabel,
              },
              type: 'linear',
              grid: {
                drawOnChartArea: false,
              },
              position: 'right',
              beginAtZero: true,
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
              },
            },
          },
        },
      });
    }
  }
}
